import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import ReactApexChart, { default as Chart } from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaymentDashboardData } from "../../../../redux/reducers/paymentDashboardSlice";
import { serviceTypes } from "../../../../util/dropList";
import "./BillingChart.css";

const BillingChart = () => {
  const dispatch = useDispatch();
  const { vetWiseBilling, top5ServiceTypesByCompletedAppointments, loading, revenueByCategory } =
    useSelector((state) => state.paymentDashboard);
  const [view, setView] = useState("year");
  const [vetBillingData, setVetBillingData] = useState([]);
  const [vetBillingNames, setVetBillingNames] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceCounts, setServiceCounts] = useState([]);
  const serviceList = serviceTypes?.map((li) => li?.name);
  const [vetList, setVetList] = useState([]);

  useEffect(() => {
    dispatch(fetchPaymentDashboardData());
  }, [dispatch]);

  useEffect(() => {
    if (!revenueByCategory || !revenueByCategory?.categories) return;
    const reqCategories = revenueByCategory?.categories?.map((li) => {
      const category = li?.category || "";
      return category?.charAt(0)?.toUpperCase() + category?.slice(1);
    });
    const reqCounts = revenueByCategory?.categories?.map((li) =>
      Number(li?.revenue ?? 0)?.toFixed(0)
    );
    setServiceCategories(reqCategories);
    setServiceCounts(reqCounts);
  }, [revenueByCategory]);

  useEffect(() => {
    if (!vetWiseBilling || !vetWiseBilling?.top5Vets) return;
    const reqVets = vetWiseBilling?.top5Vets?.map((li) => {
      const name = li?.vetName || ''
      return name?.charAt(0)?.toUpperCase() + name?.slice(1);
    })
    setVetList(reqVets);
  }, [vetWiseBilling]);

  const ageSeries = useMemo(() => {
    const reqVets = vetWiseBilling?.top5Vets?.map((li) => {
      const name = li?.vetName || ''
      return name?.charAt(0)?.toUpperCase() + name?.slice(1);
    })
    const vetsToUse = reqVets
    const servicesToUse = serviceList

    if (!vetsToUse?.length || !servicesToUse?.length) return [];

    // Create one series per service type (7 series total)
    // Each series contains revenue data for all vets
    const seriesData = servicesToUse.map((serviceName) => {
      // For each service type, get revenue for each vet
      const revenueData = vetsToUse?.map((vetName) => {
        // If we have real data, use it
        if (vetWiseBilling?.top5Vets?.length) {
          const vet = vetWiseBilling?.top5Vets?.find(
            (tv) => tv?.vetName?.toLowerCase() === vetName?.toLowerCase()
          );

          if (!vet?.serviceTypes?.length) return 0;

          // Find the specific service type revenue for this vet
          const serviceData = vet.serviceTypes.find(
            (st) => st?.serviceType?.toLowerCase() === serviceName?.toLowerCase()
          );

          return Number(serviceData?.revenue ?? 0)?.toFixed(0) ?? 0;
        }

        return 0;
      });

      return {
        name: serviceName,
        data: revenueData, // Array length matches vetsToUse length
      };
    });

    return seriesData;
  }, [vetWiseBilling, serviceList, vetList]);

  const ageOptions = useMemo(() => {
    const vetsToUse = vetList
    const servicesToUse = serviceList

    if (!vetsToUse?.length || !servicesToUse?.length) return {};

    return {
      chart: { type: "bar", stacked: true, toolbar: { show: false } },
      plotOptions: {
        bar: { horizontal: false, columnWidth: "40%", borderRadius: 10 },
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: "13px", fontWeight: "bold", colors: ["#fff"] },
      },
      colors: [
        "#004AAD",
        "#A7EDE7",
        "#000000",
        "#90C2FF",
        "#D3D3F3",
        "#FF69B4",
        "#FFA500",
      ],
      xaxis: { categories: vetsToUse },
      legend: { position: "top", horizontalAlign: "left" },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const vetsToUse = vetList;
          const servicesToUse = serviceList

          const vetName = vetsToUse?.[dataPointIndex] || "";
          const serviceType = servicesToUse?.[seriesIndex] || "";

          const hoverData = vetWiseBilling?.top5Vets?.[dataPointIndex]
          const hoverServiceData = hoverData?.serviceTypes?.[seriesIndex]

          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            vetName +
            " - " +
            serviceType +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            w.globals.colors[seriesIndex] +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Revenue: ₹</span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(hoverServiceData?.revenue || 0)?.toFixed(2) +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Revenue %: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(hoverServiceData?.revenuePercentage || 0)?.toFixed(1) + '%' +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Service Time in Minutes: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(hoverServiceData?.serviceTimeMinutes || 0)?.toFixed(0) + 'm' +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            `<span class="apexcharts-tooltip-text-y-label">Total service time = ${hoverServiceData?.serviceTimeMinutes ?? 0}m, Revenue percentage = ${Number(hoverServiceData?.revenuePercentage || 0)?.toFixed(1)}%</span>` +
            "</div>" +
            "</div>" +
            "</div>"
          );
        },
      },
    };
  }, [vetList, serviceList, vetWiseBilling]);

  const revenueByCategoryObj = {
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 4,
          columnWidth: "10%", // Set to 20% to limit bar width (adjust as needed: lower = thinner bars)
        },
      },
      colors: ["#004AAD", "#A7EDE7", "#000000", "#90C2FF", "#D3D3F3"],
      xaxis: {
        categories: serviceCategories,
        labels: {
          style: { fontSize: "14px", colors: "#000" },
        },
      },
      yaxis: {
        labels: { style: { fontSize: "12px" } },
      },
      dataLabels: { enabled: false },
      grid: { yaxis: { lines: { show: false } } },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const category = serviceCategories?.[dataPointIndex] || '';
          // const value = series[seriesIndex][dataPointIndex];
          const reqData = revenueByCategory?.categories?.find((li) => li?.category?.toLowerCase() === category?.toLowerCase()) ?? {}
          const reqStr = `₹${Number((reqData?.revenue ?? 0) / 100000)?.toFixed(2)}L revenue, avg bill ₹${Number(reqData?.avgBill ?? 0)?.toFixed(2)}`

          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            category +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            w.globals.colors[dataPointIndex] +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Appointments: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            reqData?.count +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            `<span class="apexcharts-tooltip-text-y-label">${reqStr}</span>` +
            "</div>" +
            "</div>" +
            "</div>"
          );
        },
      },
    },
    series: [
      {
        name: "Appointments",
        data: serviceCounts,
      },
    ],
  };

  const billingContribution = {
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          barHeight: "40%",
          dataLabels: {
            position: "top",
          },
        },
      },
      colors: ["#004AAD", "#9E9EAF", "#C4C4D3", "#EAEAF6"],
      xaxis: {
        categories: vetBillingNames,
        labels: {
          style: { fontSize: "14px", colors: "#000" },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (_, { dataPointIndex }) =>
          vetBillingData[dataPointIndex].toLocaleString(),
        textAnchor: "start",
        offsetX: 20,
        style: {
          colors: ["#004AAD", "#000", "#000", "#000"],
          fontSize: "14px",
          fontWeight: 600,
        },
      },
      grid: { xaxis: { lines: { show: false } } },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const vetName = vetBillingNames[dataPointIndex] || '';
          const value = series[seriesIndex][dataPointIndex];

          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            vetName +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            w.globals.colors[dataPointIndex] +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Billing: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            value.toLocaleString() +
            "</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        },
      },
    },
    series: [
      {
        name: "Billing",
        data: vetBillingData,
      },
    ],
  };

  return (
    <div className="dashboard-wrapper">
      {/* Top-left Toggle Buttons */}
      {/* <div className="time-toggle-container">
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, val) => val && setView(val)}
          size="small"
          sx={{
            backgroundColor: "#f0f2f5",
            borderRadius: "12px",

            // Apply to each toggle button in the group
            "& .MuiToggleButtonGroup-grouped": {
              borderRadius: "7px",
              borderColor: "transparent",
              color: "#333",

              // Apply only when selected
              "&.Mui-selected": {
                backgroundColor: "#005EB8",
                color: "#fff",
              },
            },
          }}
        >
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="year">Year</ToggleButton>
        </ToggleButtonGroup>
      </div> */}

      {/* Chart Section */}
      <div className="dashboard-container">
        {/* Chart 1 */}
        <div className="chart-section">
          <h4>Revenue by Category</h4>
          <Chart
            options={revenueByCategoryObj.options}
            series={revenueByCategoryObj.series}
            type="bar"
            height={300}
          />
        </div>

        {/* Chart 2 */}
        <div className="chart-section">
          <h4>Vet-wise Billing Contribution</h4>
          <ReactApexChart
            options={ageOptions}
            series={ageSeries}
            type="bar"
            height={300}
          />
          {/* <Chart
            options={billingContribution.options}
            series={billingContribution.series}
            type="bar"
            height={300}
          /> */}
        </div>
      </div>
    </div>
  );
};

export default BillingChart;
